import { useEffect, useState } from 'react';
import { WEATHER_CREDIT, formatTemp, weatherIcon, type WeatherReport } from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';

/** So oft fragt die Event-Seite nach -- der Server holt ohnehin nur alle 30 Minuten neu. */
const REFRESH_MS = 10 * 60_000;

const mm = (value: number) => `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(value)} mm`;

/**
 * Wetter am Event-Ort fuer die Event-Seite: jetzt, die naechsten zwoelf
 * Stunden und drei Tage. Gibt es keine Vorhersage (kein Ort, Dienst aus),
 * steht hier nichts -- ein leerer Kasten haelt niemanden warm.
 */
export function WeatherCard({ slug }: { slug: string }) {
  const [report, setReport] = useState<WeatherReport | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .getWeather(slug)
        .then(({ weather }) => alive && setReport(weather.report))
        .catch(() => undefined);
    void load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [slug]);

  if (!report) return null;
  const now = weatherIcon(report.now.symbol);
  // Ist es die ganzen zwoelf Stunden trocken, stuende unter jeder nur ein Strich.
  const wet = report.hours.some((hour) => hour.precipMm > 0);

  return (
    <div>
      <h2 className="detail-h2">Wetter</h2>
      <div className="weather-card">
        <div className="weather-card__now">
          <span className="weather-card__icon" aria-hidden>
            {now.icon}
          </span>
          <b className="weather-card__temp">{formatTemp(report.now.temp)}</b>
          <div>
            <div className="weather-card__place">{report.place}</div>
            <div className="weather-card__meta">
              {[now.text, `Wind ${report.now.windKmh} km/h`, report.now.precipMm > 0 ? mm(report.now.precipMm) : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </div>

        <div className="weather-card__hours" aria-label="Die nächsten Stunden">
          {report.hours.map((hour) => (
            <div className="weather-card__hour" key={hour.at}>
              <span>{hour.label}</span>
              <span className="weather-card__icon" aria-label={weatherIcon(hour.symbol).text}>
                {weatherIcon(hour.symbol).icon}
              </span>
              <b>{formatTemp(hour.temp)}</b>
              {wet && <span className="weather-card__rain">{hour.precipMm > 0 ? mm(hour.precipMm) : '–'}</span>}
            </div>
          ))}
        </div>

        <ul className="weather-card__days">
          {report.days.map((day) => (
            <li key={day.label}>
              <span className="weather-card__day">{day.label}</span>
              <span aria-label={weatherIcon(day.symbol).text}>{weatherIcon(day.symbol).icon}</span>
              <b>
                {formatTemp(day.tempMax)} <span className="weather-card__low">/ {formatTemp(day.tempMin)}</span>
              </b>
              <span className="weather-card__rain">{day.precipMm > 0 ? mm(day.precipMm) : 'trocken'}</span>
            </li>
          ))}
        </ul>

        <p className="weather-card__credit">
          {WEATHER_CREDIT} (
          <a href="https://www.yr.no" target="_blank" rel="noreferrer">
            yr.no
          </a>
          , CC BY 4.0) · Ort: © OpenStreetMap-Mitwirkende
        </p>
      </div>
    </div>
  );
}
